import { useCallback, useEffect, useState } from "react";
import http from "../../../../services/http";

export interface SupportCategory {
	id: string;
	code: string;
	label: string;
	applicableTo: "customer" | "station" | "both";
	isActive: boolean;
	sortOrder: number;
	bookingContext: "with_booking" | "without_booking";
	defaultPriority: string | null;
}

export function useSupportCategories() {
	const [categories, setCategories] = useState<SupportCategory[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");

	const load = useCallback(async () => {
		setLoading(true);
		setError("");
		try {
			const { data } = await http.get("/admin/support-ticket-categories");
			setCategories(data.categories ?? []);
		} catch (e: any) {
			setError(e?.response?.data?.message || e?.message || "Load failed");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		void load();
	}, [load]);

	const create = async (payload: Omit<SupportCategory, "id">) => {
		await http.post("/admin/support-ticket-categories", payload);
		await load();
	};

	const update = async (id: string, patch: Partial<Omit<SupportCategory, "id" | "code">>) => {
		await http.patch(`/admin/support-ticket-categories/${id}`, patch);
		await load();
	};

	const remove = async (id: string, force = false) => {
		const params = force ? { force: "1" } : undefined;
		await http.delete(`/admin/support-ticket-categories/${id}`, { params });
		await load();
	};

	return { categories, loading, error, load, create, update, remove };
}
